/**
 * @prettier
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { browserHistory } from 'react-router';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { STEPS } from 'cards/constants/Steps';
import { CARD_TYPES } from 'cards/constants/CardTypes';

import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import { CTAV2 } from '@zola/zola-ui/src/components/PreAuth/CTAV2';
import { hideModal } from 'cards/actions/modalActions';
import SingleDraft from './components/SingleDraft';
import MultipleDrafts from './components/MultipleDrafts';

import { getStepUrl } from '../../../../util/getStepUrl';

// @ts-ignore
import styles from './ExistingDraftsForCardSuiteModal.module.less';

/**
 * @param {number} draftAmount
 * @param {string} cardSuiteName
 * @return {string} Title
 */
const generateTitle = (draftAmount, cardSuiteName) =>
  `You already have ${draftAmount > 1 ? `${draftAmount} drafts` : ' a draft'} in ${cardSuiteName}`;

/**
 * @param {Object} props - Component props
 * @param {Array} props.relatedDrafts - Related drafts for this item
 * @param {() => void} props.onStartNewProject - On start new project callback
 * @param {Boolean} props.isDstd - Whether drafts are for digital save the date
 */
const ExistingDraftsForCardSuiteModal = ({ relatedDrafts, onStartNewProject, isDstd = false }) => {
  const [selectedItemUUID, setSelectedItemUUID] = useState(relatedDrafts[0].project_uuid);
  const [isOrderMoreAvailable, setIsOrderMoreAvailable] = useState(
    relatedDrafts[0].order_more_available
  );
  const titleClasses = cx(styles.title, { [styles.single]: relatedDrafts.length === 1 });

  const dispatch = useDispatch();

  useEffect(() => {
    setIsOrderMoreAvailable(
      relatedDrafts
        .filter(draft => draft.project_uuid === selectedItemUUID)
        .map(draft => draft.order_more_available)
    );
  }, [selectedItemUUID, relatedDrafts]);

  const openExistingDraft = useCallback(() => {
    const draftUrl = getStepUrl(
      selectedItemUUID,
      isDstd ? STEPS.front : '',
      isDstd ? CARD_TYPES.dstd : ''
    );
    dispatch(hideModal());
    browserHistory.push(draftUrl);
  }, [selectedItemUUID, isDstd]);

  return (
    <div className={styles.modal}>
      <div className={styles.body}>
        <h3 className={titleClasses}>
          {generateTitle(relatedDrafts.length, relatedDrafts[0].project_name)}
        </h3>
        {relatedDrafts.length === 1 ? (
          <SingleDraft draft={relatedDrafts[0]} />
        ) : (
          <MultipleDrafts
            drafts={relatedDrafts}
            selectedItemUUID={selectedItemUUID}
            onSelectionChanged={uuid => setSelectedItemUUID(uuid)}
          />
        )}
        <div className={styles.actions}>
          <CTAV2
            className={styles.cta}
            arrow={false}
            component="button"
            // @ts-ignore
            variant="secondary"
            onClick={onStartNewProject}
          >
            Start New
          </CTAV2>
          <div className={styles.separator} />
          <ButtonV2
            disabled={!isOrderMoreAvailable}
            data-tooltip={
              !isOrderMoreAvailable
                ? 'This combination is no longer available. Please create a new draft.'
                : null
            }
            onClick={openExistingDraft}
          >
            Edit Draft
          </ButtonV2>
        </div>
      </div>
    </div>
  );
};

ExistingDraftsForCardSuiteModal.propTypes = {
  relatedDrafts: PropTypes.arrayOf(
    PropTypes.shape({
      project_name: PropTypes.string,
      project_uuid: PropTypes.string,
    })
  ),
  onStartNewProject: PropTypes.func,
};

export default ExistingDraftsForCardSuiteModal;
